import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { useFormikContext } from 'formik';
import { Field, getIn } from 'formik';
import { Select as BaseSelect } from '../base';
import { ClickAwayListener, FormLabel, MenuItem, Popper } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { getValueType, isEmptyArray, isNil, makeUniqueTestId } from 'app/utils';
import { IntlMessage } from 'app/components/Intl';
import { InputAdornmentStyled, TooltipStyled } from './Select.styles';
import { getSelectActualValue } from './formikFieldHelpers';
import { getDefaultOption, normalizeOptions } from '../Shared/formikFieldHelpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import type { SelectProps } from './Select';
import { Skeleton } from '../../Skeleton';
import { useFieldValidator } from '../Factory';

export const SELECT_DATA_TEST_ID = 'select-component';

type InternalSelectProps = SelectProps & {
  formik: ReturnType<typeof useFormikContext>;
};

const InternalSelect = ({ onChange, field, variant = 'default', formik, className, testId }: InternalSelectProps) => {
  const {
    label,
    name,
    value,
    header,
    defaultValue,
    required,
    renderInfo,
    loading = false,
    disabled = false,
    disableOnChange = false,
    fullWidth = true,
    displayEmptyOption = false,
    tooltipText = false,
    ...rest
  } = field || {};

  // should be ignored!
  delete rest.options;
  delete rest.schema;
  delete rest.items;

  const { initialValues, setFieldValue, setFieldTouched, isSubmitting, values: formikValues } = formik;
  const formikValue = getIn(formikValues, name);

  const normalizedOptions = useMemo(() => {
    return normalizeOptions({ field, loading });
  }, [field, loading]);

  const defaultOption = useMemo(() => {
    return getDefaultOption({
      normalizedOptions,
      defaultValue,
    });
  }, [defaultValue, normalizedOptions]);

  const actualDefaultValue = useMemo(() => {
    const initialValue = getIn(initialValues, name);
    return isNil(initialValue) ? (isNil(defaultOption?.value) ? '' : defaultOption.value) : initialValue;
  }, [initialValues, name, defaultOption?.value]);

  const actualValue = useMemo(() => {
    return getSelectActualValue({
      normalizedOptions,
      defaultValue: actualDefaultValue,
      formikValue,
      value,
    });
  }, [actualDefaultValue, normalizedOptions, value, formikValue]);

  const actualDisabled = loading || disabled || isSubmitting;
  const [currentValue, setCurrentValue] = useState(actualValue);
  const [fieldDisabled, setFieldDisabled] = useState(actualDisabled);
  const [infoPopperAnchorElement, setInfoPopperAnchorElement] = useState<HTMLDivElement | null>(null);
  const containerDivRef = useRef<HTMLDivElement | null>(null);
  const valueType = getValueType(currentValue);
  const hasEmptyOption = !!(displayEmptyOption && normalizedOptions.find(option => option.value === ''));
  const showInfoIcon = !!renderInfo && !!actualValue;
  const validator = useFieldValidator('select');

  useEffect(() => {
    setFieldDisabled(actualDisabled);
  }, [actualDisabled]);

  useEffect(() => {
    setCurrentValue(actualValue);
  }, [actualValue]);

  const onInfoIconClick = useCallback(() => {
    setInfoPopperAnchorElement(infoPopperAnchorElement ? null : containerDivRef.current);
  }, [infoPopperAnchorElement]);

  const endAdornment = useMemo(() => {
    if (showInfoIcon) {
      return (
        <InputAdornmentStyled slim={variant === 'slim'} position="end">
          <InfoIcon onClick={onInfoIconClick} />
        </InputAdornmentStyled>
      );
    }
    return undefined;
  }, [onInfoIconClick, showInfoIcon, variant]);

  const selectItems = useMemo(() => {
    const renderValue = (option?: any) => {
      if (loading) {
        return <Skeleton />;
      }
      const valueToDisplay = option?.element ? option?.element : option?.value || '';
      return <IntlMessage value={valueToDisplay} />;
    };

    if (loading && isEmptyArray(normalizedOptions)) {
      return <MenuItem value={''}>{renderValue()}</MenuItem>;
    }

    return normalizedOptions.map((option, index) => (
      <MenuItem key={index} value={option?.value} disabled={option?.disabled}>
        {renderValue(option)}
      </MenuItem>
    ));
  }, [loading, normalizedOptions]);

  const change = useCallback(
    async (event, child) => {
      const {
        target: { value },
      } = event;
      if (disableOnChange) {
        setFieldDisabled(true);
      }

      const newValue = isNil(value) ? '' : value;
      setCurrentValue(newValue);
      // needs to be performed on this order!
      setFieldTouched(name, true, false);
      await setFieldValue(name, newValue, true);
      onChange && onChange({ event, child, name, value: newValue, setFieldValue });
    },
    [setFieldValue, setFieldTouched, name, onChange, disableOnChange],
  );

  const displayEmpty = !!defaultOption || loading || hasEmptyOption;
  const isEmpty = currentValue === '';
  const size = variant === 'slim' ? 'small' : undefined;
  // notch is the white background behind the label when it's shrunk
  const hasNotch = !!(((!!defaultOption && isEmpty) || !isEmpty || loading || hasEmptyOption) && label);

  const formControlProps = useMemo(() => {
    return {
      fullWidth: fullWidth,
      required: required,
      size,
    };
  }, [fullWidth, size, required]);

  const inputLabelProps = useMemo(() => {
    const shrink = displayEmpty || !isEmpty;
    const isRequiredAndHasLabel = !!label && required;

    return {
      shrink,
      size,
      required: isRequiredAndHasLabel,
    };
  }, [displayEmpty, isEmpty, label, size, required]);

  const validate = useCallback(
    value => {
      return validator(value, field);
    },
    [field, validator],
  );

  // Custom onClose handler that doesn't trigger validation.
  // Validation is already handled by setFieldValue in the change handler.
  const handleClose = useCallback(() => {}, []);
  const resolvedTestId = testId || makeUniqueTestId('select', name);

  return (
    <>
      <div className={className} data-testid={resolvedTestId}>
        {header && (
          <FormLabel>
            <IntlMessage value={header} /> {required ? ' *' : ''}
            {tooltipText && (
              <TooltipStyled title={<IntlMessage value={tooltipText} />}>
                <FontAwesomeIcon icon={faQuestionCircle} />
              </TooltipStyled>
            )}
          </FormLabel>
        )}
        <Field
          data-testid={SELECT_DATA_TEST_ID}
          disabled={fieldDisabled}
          component={BaseSelect}
          name={name}
          label={label}
          value={currentValue}
          onChange={change}
          onClose={handleClose}
          formControl={formControlProps}
          inputLabel={inputLabelProps}
          displayEmpty={displayEmpty}
          notched={hasNotch}
          valueType={valueType}
          {...rest}
          size={size}
          endAdornment={endAdornment}
          validate={validate}
        >
          {selectItems}
        </Field>
      </div>
      {showInfoIcon && (
        // https://github.com/mui/material-ui/issues/35287
        // @ts-ignore
        <Popper
          open={Boolean(infoPopperAnchorElement)}
          anchorEl={infoPopperAnchorElement}
          placement={'bottom-end'}
          popperOptions={{
            modifiers: [
              {
                name: 'offset',
                enabled: true,
                options: {
                  offset: [0, 6],
                },
              },
            ],
          }}
        >
          <ClickAwayListener onClickAway={onInfoIconClick}>
            <div>{renderInfo(actualValue)}</div>
          </ClickAwayListener>
        </Popper>
      )}
    </>
  );
};

export default InternalSelect;
